import React, { useEffect, useState } from 'react';
import { getSettings } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Settings, Users } from 'lucide-react';
import { toast } from 'sonner';
import { GeneralTab } from '../components/parametres/GeneralTab';
import { UsersTab } from '../components/parametres/UsersTab';
import { TemplatesTab } from '../components/parametres/TemplatesTab';
import { CommissionsTab } from '../components/parametres/CommissionsTab';
import { AuditLogTab } from '../components/parametres/AuditLogTab';

export const ParametresPage = () => {
    const { user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await getSettings();
                setSettings(res.data);
            } catch {
                toast.error('Erreur lors du chargement');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading || !settings) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const isAdmin = user?.role === 'admin';

    return (
        <div className="space-y-4 sm:space-y-6" data-testid="parametres-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight">Paramètres</h1>
                <p className="text-sm text-[#52525B] mt-1">Configurez votre CRM UNeedServices</p>
            </div>

            <Tabs defaultValue="general" className="w-full">
                <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                    <TabsList className="bg-[#F4F4F5] p-1 rounded-sm inline-flex flex-nowrap w-max sm:w-auto">
                        <TabsTrigger value="general" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap">
                            <Settings className="w-4 h-4 mr-2" /> Général
                        </TabsTrigger>
                        {isAdmin && (
                            <TabsTrigger value="users" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap">
                                <Users className="w-4 h-4 mr-2" /> Utilisateurs
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="templates" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap">
                            <FileText className="w-4 h-4 mr-2" /> Templates
                        </TabsTrigger>
                        {isAdmin && (
                            <TabsTrigger value="commissions" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-commissions">
                                <Settings className="w-4 h-4 mr-2" /> Commissions
                            </TabsTrigger>
                        )}
                        {isAdmin && (
                            <TabsTrigger value="audit" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-audit">
                                <FileText className="w-4 h-4 mr-2" /> Audit
                            </TabsTrigger>
                        )}
                    </TabsList>
                </div>

                <TabsContent value="general" className="mt-4">
                    <GeneralTab settings={settings} setSettings={setSettings} />
                </TabsContent>

                {isAdmin && (
                    <TabsContent value="users" className="mt-4">
                        <UsersTab currentUser={user} />
                    </TabsContent>
                )}

                <TabsContent value="templates" className="mt-4">
                    <TemplatesTab />
                </TabsContent>

                {isAdmin && (
                    <TabsContent value="commissions" className="mt-4">
                        <CommissionsTab settings={settings} setSettings={setSettings} />
                    </TabsContent>
                )}

                {isAdmin && (
                    <TabsContent value="audit" className="mt-4">
                        <AuditLogTab />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
};
